/**
 * Попап: клик мимо и Esc закрывают — один хук на кабинет.
 *
 * До 14.10.26 та же пара слушателей стояла в шестнадцати компонентах (аудит
 * фронта 12.10.26, §5.2): списки `Combo`/`PeriodPicker`/`WindowPicker`,
 * календари, шестерёнки витрины и навигации, меню тегов, QR-папок, смарт-кнопок.
 * Правило одно, и держать его в одном месте дешевле, чем в шестнадцати.
 *
 * `mousedown`, А НЕ `click`: щелчок по соседней кнопке тулбара должен и
 * закрыть попап, и сработать сам — на `click` он проиграл бы закрытию.
 *
 * Внутри попапа — всё, что лежит в `boxes`: меню, нарисованное порталом вне
 * кнопки, передаёт обе ссылки, иначе клик по его пункту закрывал бы его как
 * «мимо». Ссылки без элемента (`current === null`) ничего не содержат.
 *
 * `escape: "capture"` — для попапа ВНУТРИ другого попапа (`ExtraColumns` в
 * шестерёнке витрины): Esc ловится на захвате и гасится, чтобы закрыть только
 * внутренний. `escape: false` — когда Esc ведёт другой механизм: стопка
 * слоёв панелей (`useEscapeLayer`) у `SupplierName`, `onKeyDown` поля у `Combo`.
 *
 * Слушатели стоят только пока `open`; `onClose` читается через ссылку, чтобы
 * эффект не переподписывался на каждом рендере.
 */

import { type RefObject, useEffect, useRef } from "react";

type Box = RefObject<HTMLElement | null>;

export function usePopover(
  open: boolean,
  onClose: () => void,
  boxes: Box | readonly Box[],
  { escape = true }: { escape?: boolean | "capture" } = {},
): void {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const inside = Array.isArray(boxes) ? (boxes as readonly Box[]) : [boxes as Box];
    const away = (event: MouseEvent) => {
      const target = event.target as Node;
      if (inside.some((box) => box.current?.contains(target))) return;
      close.current();
    };
    const capture = escape === "capture";
    const esc = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (capture) event.stopPropagation();
      close.current();
    };
    document.addEventListener("mousedown", away);
    if (escape) document.addEventListener("keydown", esc, capture);
    return () => {
      document.removeEventListener("mousedown", away);
      if (escape) document.removeEventListener("keydown", esc, capture);
    };
    // `boxes` — ссылки `useRef`, они стабильны; литерал массива на каждом
    // рендере новый, и в зависимостях он переподписывал бы слушатели зря.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, escape]);
}
